import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquare, X } from "lucide-react";

interface TaskSearchResult {
  id: string;
  title: string;
  type: string;
  boardName: string;
}

interface TaskSearchSelectProps {
  selectedId: string | null;
  selectedLabel: string;
  onSelect: (id: string, label: string) => void;
  onClear: () => void;
  enabled?: boolean;
  showLabel?: boolean;
  label?: string;
  placeholder?: string;
  testIdPrefix?: string;
}

export function TaskSearchSelect({
  selectedId,
  selectedLabel,
  onSelect,
  onClear,
  enabled = true,
  showLabel = true,
  label = "Linked Task/Meeting",
  placeholder = "Search tasks/meetings...",
  testIdPrefix = "search-select",
}: TaskSearchSelectProps) {
  const [search, setSearch] = useState("");

  const { data: searchData } = useQuery<{ tasks: TaskSearchResult[] }>({
    queryKey: ["/api/tasks/search?search=" + encodeURIComponent(search) + "&limit=5"],
    enabled: search.length >= 2 && enabled,
  });

  function handleSelect(task: TaskSearchResult) {
    onSelect(task.id, `${task.boardName}: ${task.title}`);
    setSearch("");
  }

  return (
    <div className="space-y-2">
      {showLabel && <Label>{label}</Label>}
      {selectedId && (
        <div className="flex flex-wrap gap-1 mb-1">
          <Badge variant="secondary" className="text-xs" data-testid={`${testIdPrefix}-badge-linked-task`}>
            <MessageSquare className="w-3 h-3 mr-1" />
            {selectedLabel}
            <button className="ml-1" onClick={onClear}>
              <X className="w-3 h-3" />
            </button>
          </Badge>
        </div>
      )}
      {!selectedId && (
        <>
          <Input
            placeholder={placeholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid={`${testIdPrefix}-input-task-search`}
          />
          {searchData?.tasks && searchData.tasks.length > 0 && (
            <div className="border rounded-md max-h-32 overflow-y-auto">
              {searchData.tasks.map(t => (
                <button
                  key={t.id}
                  className="w-full text-left px-2 py-1 text-sm hover-elevate"
                  onClick={() => handleSelect(t)}
                  data-testid={`${testIdPrefix}-button-link-task-${t.id}`}
                >
                  <span className="text-muted-foreground">{t.boardName}:</span> {t.title}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
